/**
 * IDDL Phase 3a — admin Engine Preview screen.
 *
 * Allows an admin to mount any V2 primitive (iddl_inbox / iddl_browser)
 * against a fresh preview attempt, stream the live event log, and
 * compute the deterministic v2 score on demand.
 *
 * Phase 1.5 shipped this for `iddl_inbox` only (CS-TA-03).
 * Phase 3a extends it to `iddl_browser` (DS-IN-01) and adds a switcher.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { api, startAttempt, adminDeleteAttempt } from "@/lib/api";
import IDDLInbox from "@/components/sims/IDDLInbox";
import IDDLBrowser from "@/components/sims/IDDLBrowser";
import IDDLAIChat from "@/components/sims/IDDLAIChat";
import IDDLAIEvaluate from "@/components/sims/IDDLAIEvaluate";
import IDDLAIDecision from "@/components/sims/IDDLAIDecision";
import IDDLAIChatAgent from "@/components/sims/IDDLAIChatAgent";
import IDDLAIEvaluateAgent from "@/components/sims/IDDLAIEvaluateAgent";
import IDDLAIDecisionAgent from "@/components/sims/IDDLAIDecisionAgent";
import IDDLFileManager from "@/components/sims/IDDLFileManager";
import IDDLSettings from "@/components/sims/IDDLSettings";
import IDDLWorkflow from "@/components/sims/IDDLWorkflow";
import IDDLEditor from "@/components/sims/IDDLEditor";
import IDDLGenericPrimitive from "@/components/sims/IDDLGenericPrimitive";
import { toast } from "sonner";
import { FlaskConical, Trash2, Calculator, AlertTriangle, Wrench } from "lucide-react";

const SHIPPED_PRIMITIVES = new Set([
  "iddl_inbox", "iddl_browser", "iddl_ai_chat", "iddl_file_manager",
  "iddl_settings", "iddl_workflow", "iddl_editor",
  // Phase 2 · AI Evaluation + Responsible AI Use renderers.
  "iddl_ai_evaluate", "iddl_ai_decision",
  // Phase 6 · agentic renderers.
  "iddl_ai_chat_agent", "iddl_ai_evaluate_agent", "iddl_ai_decision_agent",
]);

// Phase 11.3 · learning-module prefix composed at the presentation layer.
// Mirrors `backend/behavioural_feedback._apply_learning_module_prefix`.
// Idempotent, dynamic from (concept_id, concept_name), applied uniformly
// to Default + non-default variants. Never persisted anywhere.
function applyLearningModulePrefix(conceptId, conceptName, remediation) {
  const cid = (conceptId || "").trim();
  const cname = (conceptName || "").trim();
  const body = (remediation || "").trim();
  if (!cid) return body;
  const prefix = cname
    ? `Refer to the learning module for ${cid} \u2013 ${cname}.`
    : `Refer to the learning module for ${cid}.`;
  const stripped = body.replace(/^\s+/, "");
  const alts = [prefix,
                prefix.replace(" \u2013 ", " - "),
                prefix.replace(" \u2013 ", " \u2014 ")];
  for (const alt of alts) {
    if (stripped.startsWith(alt)) return body;
  }
  return body ? `${prefix} ${body}` : prefix;
}

// Phase 11.4 · variant → primitive scenario adapter (frontend copy of
// `backend/variants.adapt_variant_scenario_for_primitive`). Engine
// Preview builds its mounted config client-side and does NOT round-
// trip through `/attempts/{aid}/current_engine`, so without this
// mirror the browser/inbox/ai-chat primitives would receive the
// variant's raw materials[] payload and render empty. Pure, no state.
function _eng(obj) {
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    return obj.en || Object.values(obj)[0] || "";
  }
  return obj || "";
}

function _adaptBrowserScenario(st) {
  const materials = st.materials || [];
  const role = _eng(st.role) || "practitioner";
  const setting = _eng(st.setting) || "the organisation";
  const taskBrief = st.task_brief
    || `As a ${role} in ${setting}, determine which materials have confirmed provenance.`;
  const vague = [], refined = [];
  materials.forEach((m, i) => {
    const iid = m.itemId || `obj_${String(i + 1).padStart(2, "0")}`;
    const desc = _eng(m.description) || `item ${iid}`;
    vague.push({
      id: `v_${iid}`,
      title: `Informal web mention · ${iid}`,
      url: `community-wiki://${iid}`,
      domain: "community-wiki.org",
      snippet: desc,
      credible: false, relevant: true,
      reason: "Anecdotal source \u00b7 no author \u00b7 no citations",
    });
    refined.push({
      id: `r_${iid}`,
      title: `Collection record · ${iid}`,
      url: `catalogue://${iid}`,
      domain: "catalogue",
      snippet: desc,
      credible: true, relevant: true,
      reason: "Authoritative institutional record with provenance chain",
    });
  });
  return {
    research_question: taskBrief,
    queries: { vague, refined },
    vague_query_hint: "A vague keyword search returns too many irrelevant results.",
    refined_query_hint: "A focused query targeting authoritative provenance records is more defensible.",
  };
}

function _adaptInboxScenario(st) {
  const materials = st.materials || [];
  const role = _eng(st.role) || "staff";
  const inboxName = `${role.toLowerCase().replace(/\s+/g, ".")}@museum.local`;
  const messages = materials.map((m, i) => {
    const iid = m.itemId || `msg-${i + 1}`;
    const desc = _eng(m.description) || `message ${i + 1}`;
    const suspicious = !!m.restricted;
    const subject = desc.includes(",") ? desc.split(",")[0].slice(0, 70) : desc.slice(0, 70);
    return {
      id: iid,
      is_phishing: suspicious,
      from_display: suspicious ? "External sender" : "Internal contact",
      from_email: suspicious ? "unknown@external.example" : "colleague@museum.local",
      from_real_domain: suspicious ? "external.example" : "museum.local",
      to: inboxName,
      received_at: "2026-08-04T09:00:00Z",
      subject,
      body: desc,
      links: [], attachments: [],
      header_red_flags: suspicious ? ["Mismatched reply-to"] : [],
    };
  });
  return { inbox_name: inboxName, messages };
}

function _adaptAiChatScenario(st) {
  const materials = st.materials || [];
  const role = _eng(st.role) || "practitioner";
  const setting = _eng(st.setting) || "the organisation";
  const taskBrief = st.task_brief || "";
  const lines = [];
  materials.forEach((m) => {
    const iid = m.itemId || "";
    const desc = _eng(m.description) || "";
    if (desc) lines.push(iid ? `${iid}: ${desc}` : desc);
  });
  const sourceMaterial = lines.length ? lines.join("\n") : taskBrief;
  return {
    source_material_title: `Collections draft materials (${role})`,
    source_material: sourceMaterial,
    audience_hint: `${setting} readers`,
    format_hint: "short note",
    weak_output_template: "Draft note (weak): claims attributed to any material without verifying provenance.",
    strong_output_template: "Draft note (refined): claims cite only materials with confirmed provenance and avoid unsupported attributions.",
    vague_signals: ["summary", "describe"],
    refinement_signals: ["bullet", "executive", "audience", "limit", "length", "format", "5", "concise"],
  };
}

const _PRIMITIVE_REQUIRED_KEYS = {
  iddl_browser: ["queries"],
  iddl_inbox:   ["messages"],
  iddl_ai_chat: ["source_material"],
};
const _PRIMITIVE_ADAPTERS = {
  iddl_browser: _adaptBrowserScenario,
  iddl_inbox:   _adaptInboxScenario,
  iddl_ai_chat: _adaptAiChatScenario,
};

function adaptVariantScenarioForPrimitive(primitive, scenarioText) {
  const st = { ...(scenarioText || {}) };
  if (!primitive || !_PRIMITIVE_ADAPTERS[primitive]) return st;
  const required = _PRIMITIVE_REQUIRED_KEYS[primitive];
  if (required.some((k) => st[k])) return st;   // already primitive-ready
  const adapted = _PRIMITIVE_ADAPTERS[primitive](st);
  return { ...st, ...adapted };
}


export default function EnginePreview() {
  const { t } = useT();
  // Phase 6 · Catalogue groups concept rows by concept_id so the
  // environment picker below can enable only environments that
  // actually have a contract for the chosen concept. Each catalogue
  // entry: { id, name, domain, subdomain, environments: [{env, sim,
  // rich, draft}] }.
  const [catalogue, setCatalogue] = useState([]);
  const [conceptId, setConceptId] = useState(null);
  const [environment, setEnvironment] = useState(null);  // 'ai' | 'agentic_ai' | null
  // Phase 10 · optional variant selector. null = Default (candidate-facing).
  const [variantList, setVariantList]  = useState([]);
  const [variantId, setVariantId]      = useState(null);
  const [variantOverride, setVariantOverride] = useState(null);  // full variant doc

  // Phase 10 · load the variants for the current (concept, environment).
  useEffect(() => {
    if (!conceptId) { setVariantList([]); return; }
    const envParam = environment === null ? "null" : environment;
    // Phase 11.1 · Engine Preview must never offer Retired variants in
    // the normal selector. Retired variants remain stored in the
    // Scenario Bank for audit, but they are not loadable for preview.
    api.get(`/admin/variants?concept_id=${conceptId}&environment=${envParam}`)
      .then((r) => setVariantList(r.data.variants || []))
      .catch(() => setVariantList([]));
    // Reset the variant override when concept/env changes.
    setVariantId(null); setVariantOverride(null);
  }, [conceptId, environment]);
  const [primitiveMap, setPrimitiveMap] = useState({});  // {`${subdomain}::${env}`: sim_type}
  const [config, setConfig] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [events, setEvents] = useState([]);
  const [scoreResult, setScoreResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Phase 11.2 · Engine Preview K + R inputs for non-default variants.
  // These feed POST /admin/variants/{id}/preview_attempt so a complete
  // K-C-R assessment is scorable from the Engine Preview surface.
  const [previewKnowledgeIndex, setPreviewKnowledgeIndex] = useState(null);
  const [previewReflectionText, setPreviewReflectionText] = useState("");
  // Phase 11 · canonical contract metadata captured at mount time
  // (fetched from `/admin/concept_configs/{cid}` so the diagnostic
  // panel shows the canonical version/hash the scorer actually uses,
  // regardless of which Default/variant is mounted).
  const [canonMeta, setCanonMeta] = useState(null);
  const startGuardRef = useRef(false);

  const refreshCatalogue = async () => {
    try {
      const r = await api.get("/admin/concept_configs").then((x) => x.data);
      // Group by concept_id → environment(s). Each concept lists all
      // env rows that carry `use_new_engine=true`.
      const byConcept = new Map();
      for (const c of r.configs || []) {
        if (!c.use_new_engine) continue;
        const entry = byConcept.get(c.concept_id) || {
          id: c.concept_id,
          name: c.concept_name,
          domain: c.domain,
          subdomain: c.subdomain,
          environments: [],
        };
        entry.environments.push({
          env: c.environment || null,
          sim: c.simulation_type || "",
          rich: SHIPPED_PRIMITIVES.has(c.simulation_type),
          draft: !!c.draft_generated,
          version: c.config_version,
          hash: c.config_hash,
        });
        byConcept.set(c.concept_id, entry);
      }
      // Phase 6 · sort each concept's environments deterministically
      // (null → ai → agentic_ai). This matches ConceptConfig, so
      // multi-env concepts default to the conventional AI contract
      // instead of the agentic PoC.
      const envOrder = (e) => (e == null ? 0 : e === "ai" ? 1 : 2);
      for (const entry of byConcept.values()) {
        entry.environments.sort((a, b) => envOrder(a.env) - envOrder(b.env));
      }
      const items = [...byConcept.values()].sort((a, b) => {
        // Concepts with at least one rich primitive first.
        const aRich = a.environments.some((e) => e.rich);
        const bRich = b.environments.some((e) => e.rich);
        return (Number(bRich) - Number(aRich)) || a.id.localeCompare(b.id);
      });
      setCatalogue(items);
      // Cache the subdomain × env → primitive map so the UI can show
      // the resolved primitive read-only.
      const pm = {};
      for (const m of r.subdomain_env_primitive_map || []) {
        pm[`${m.subdomain}::${m.environment}`] = m.simulation_type;
      }
      setPrimitiveMap(pm);
      return items;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Failed";
      toast.error(`Could not load concept catalogue: ${detail}`);
      return [];
    }
  };

  // Iter-11 — admins can preview the country-contextualized scenario
  // for a candidate from a specific country (matters for DS-IN-01).
  // Defaults to "United Kingdom" so the rehearsal looks identical to
  // a real GB candidate; switching the picker re-fetches the scenario.
  const [previewCountry, setPreviewCountry] = useState("United Kingdom");
  // Iter-26 (P1) — admin can preview any concept in any supported
  // language. Defaults to EN; switching the picker re-fetches the
  // runtime_config so the localised content swaps in place without
  // restarting the attempt.
  const [previewLang, setPreviewLang] = useState("en");

  const start = async (cid, envOverride, countryOverride, langOverride, variantDoc) => {
    setLoading(true); setErr(null); setEvents([]); setScoreResult(null); setConfig(null); setAttempt(null);
    const country = countryOverride || previewCountry || "United Kingdom";
    const lang = langOverride || previewLang || "en";
    const env = envOverride !== undefined ? envOverride : environment;
    try {
      // Phase 11 · fetch canonical admin metadata FIRST so we always
      // have the canonical capability contract version/hash + the
      // canonical remediation message, regardless of which variant is
      // mounted. Scoring runs through this canonical contract.
      let adminFull = null;
      try {
        const fullParams = env ? { environment: env } : {};
        adminFull = await api
          .get(`/admin/concept_configs/${cid}`, { params: fullParams })
          .then((x) => x.data);
      } catch (_e) { /* non-fatal — preview still works */ }
      setCanonMeta(adminFull ? {
        concept_id:    adminFull.concept_id,
        concept_name:  adminFull.concept_name,
        environment:   adminFull.environment,
        primitive:     adminFull.simulation_type,
        config_version: adminFull.config_version,
        config_hash:   adminFull.config_hash,
      } : null);

      let cfg;
      if (variantDoc && !variantDoc.is_default) {
        // Phase 11 · mount the variant's own K-C-R presentation payload.
        // Capability contract and scoring remain canonical.
        const cap = variantDoc.capability || {};
        // Phase 11.4 · project the variant's generic materials-based
        // payload onto the primitive's expected scenario_text keys so
        // the Engine Preview primitive renders the variant's content
        // (previously it received the raw materials[] payload and
        // showed an empty browser/inbox/chat). Pure, no state.
        const adaptedScenario = adaptVariantScenarioForPrimitive(
          variantDoc.primitive, cap.scenario_text || {},
        );
        cfg = {
          use_new_engine:   true,
          concept_id:       variantDoc.concept_id,
          concept_name:     variantDoc.concept_name,
          simulation_type:  variantDoc.primitive,
          environment:      variantDoc.environment,
          scenario_text:    adaptedScenario,
          candidate_goal:   cap.candidate_goal || "",
          candidate_task:   cap.candidate_task || "",
          available_actions:    cap.available_actions || [],
          valid_final_decisions: cap.valid_final_decisions || [],
          // Diagnostic panel shows canonical version/hash separately.
          config_version:   adminFull?.config_version,
          config_hash:      adminFull?.config_hash,
          candidate_country: country,
          resolved_lang:    lang,
          requested_lang:   lang,
          _variant: {
            variant_id:     variantDoc.variant_id,
            variant_name:   variantDoc.variant_name,
            country:        variantDoc.country,
            status:         variantDoc.status,
            knowledge:      variantDoc.knowledge,
            reflection:     variantDoc.reflection,
            remediation_message: variantDoc.remediation_message,
          },
        };
      } else {
        // Default (candidate-facing) path — unchanged.
        const params = { country, lang };
        if (env) params.environment = env;
        cfg = await api
          .get(`/concepts/${cid}/runtime_config`, { params })
          .then((x) => x.data);
      }
      if (adminFull) {
        cfg._admin_full = {
          // Phase 11 · canonical remediation only — the learning-module
          // prefix is composed at display time in RemediationReview.
          remediation_message: adminFull.remediation_message || "",
          required_events: adminFull.required_events || [],
          critical_merit_events: adminFull.critical_merit_events || [],
          auto_fail_events: adminFull.auto_fail_events || [],
          valid_final_decisions: adminFull.valid_final_decisions || [],
          sequence_rules: adminFull.sequence_rules || [],
        };
      }
      setConfig(cfg);
      const a = await startAttempt({
        name: `Preview-${cid}-${env || "default"}-${variantDoc?.variant_id || "default"}-${new Date().toISOString().slice(0, 19)}`,
        country,
        preview: true,
      });
      setAttempt(a);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Failed";
      setErr(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startGuardRef.current) return;
    startGuardRef.current = true;
    (async () => {
      const items = await refreshCatalogue();
      if (items.length > 0) {
        // Prefer a concept whose FIRST env has a rich primitive.
        const first = items.find((c) => c.environments.some((e) => e.rich)) || items[0];
        // Pick the rich environment if present, else the first.
        const richEnv = first.environments.find((e) => e.rich) || first.environments[0];
        setConceptId(first.id);
        setEnvironment(richEnv.env);
        start(first.id, richEnv.env);
      } else {
        setErr("No concept has use_new_engine=true. Activate one from the Assessment Contracts tab.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEvent = (ev) => {
    setEvents((prev) => [...prev, ev]);
  };

  const computeScore = async () => {
    if (!attempt) return;
    try {
      // Phase 11.2 · for non-default variants in Engine Preview, route
      // through the variant preview_attempt endpoint so the Knowledge
      // (variant.knowledge) and Reflection (variant.reflection) layers
      // are also scored alongside Capability. Default variants keep
      // the existing attempts/score path, which is Capability-only
      // (matching current Default Engine Preview behaviour).
      if (variantOverride && !variantOverride.is_default) {
        const r = await api.post(
          `/admin/variants/${variantOverride.variant_id}/preview_attempt`,
          {
            events: events.map(e => ({
              event: e.event_name || e.event,
              time:  e.event_time || e.time,
              detail: e.detail,
              payload: e.event_value || e.payload,
            })),
            knowledge_answer_index: previewKnowledgeIndex,
            reflection_text:        previewReflectionText,
          },
        ).then(x => x.data);
        setScoreResult({
          // Normalise to the shape RemediationReview expects.
          score: {
            outcome:              r.capability.outcome,
            reasons:              [],
            final_decision:       null,
            required_satisfied:   (r.capability_evidence || {}).required_events || [],
            critical_satisfied:   (r.capability_evidence || {}).critical_events || [],
            auto_fail_fired:      ((r.capability_evidence || {}).auto_fail_triggered ? [(r.capability_evidence || {}).auto_fail_triggered] : []),
          },
          layers: {
            knowledge:  r.knowledge,
            capability: r.capability,
            reflection: r.reflection,
          },
          variant_outcome:   r.outcome,
          feedback:          r.behavioural_feedback,
        });
        return;
      }
      const params = environment ? { environment } : {};
      const s = await api
        .get(`/attempts/${attempt.id}/concepts/${conceptId}/score`, { params })
        .then((x) => x.data);
      setScoreResult(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Score failed");
    }
  };

  const purgeAndRestart = async (nextConceptId, nextEnv, nextCountry, nextLang, nextVariant) => {
    const targetCid = nextConceptId || conceptId;
    let targetEnv = nextEnv !== undefined ? nextEnv : environment;
    if (attempt) {
      try { await adminDeleteAttempt(attempt.id); } catch (e) { /* non-fatal */ }
    }
    startGuardRef.current = false;
    setAttempt(null); setEvents([]); setScoreResult(null); setConfig(null); setErr(null);
    // Phase 11.2 · reset per-variant K/R inputs on every re-mount.
    setPreviewKnowledgeIndex(null); setPreviewReflectionText("");
    // Phase 11 · when a variant has NOT been passed in explicitly,
    // clear any current variant override so concept/env/country/lang
    // switches never silently keep a stale variant mounted.
    if (nextVariant === undefined) { setVariantId(null); setVariantOverride(null); }
    setConceptId(targetCid);
    // Refresh catalogue every switch so admin toggles in another tab
    // become visible without a page reload.
    const items = await refreshCatalogue();
    // If concept just changed, pick the concept's first env unless
    // caller supplied one.
    if (nextConceptId && nextEnv === undefined) {
      const entry = items.find((c) => c.id === targetCid);
      const richEnv = entry?.environments.find((e) => e.rich) || entry?.environments[0];
      targetEnv = richEnv?.env ?? null;
    }
    setEnvironment(targetEnv);
    // Phase 11 · a null `nextVariant` means "Default"; `undefined` means
    // "keep whatever is currently selected" (but Default when concept
    // changes, see onChange handlers below).
    const vd = nextVariant === undefined ? null : nextVariant;
    setTimeout(() => { startGuardRef.current = true; start(targetCid, targetEnv, nextCountry, nextLang, vd); }, 0);
  };

  const totalDwellMs = useMemo(
    () => events.reduce((acc, e) => acc + (e.dwell_ms || 0), 0),
    [events],
  );
  const reversals = events.filter((e) => e.is_reversal).length;
  const autoFails = events.filter((e) => e.is_auto_fail_event).length;

  return (
    <div className="mt-6" data-testid="engine-preview-tab">
      <div className="flex items-center gap-3 text-sm text-[var(--iddl-muted)] mb-4">
        <FlaskConical size={14} />
        <span>
          <strong>V2 Engine preview.</strong> Pick a concept below to mount
          its primitive against a fresh preview attempt. Real clicks emit
          real events; compute the deterministic v2 score at any point.
          The candidate-facing flow is unchanged unless `use_new_engine`
          is set in `concept_configs`.
        </span>
      </div>

      <div className="flex items-center flex-wrap gap-3 mb-4">
        <label className="label-mono" htmlFor="engine-preview-concept">Concept:</label>
        <select
          id="engine-preview-concept"
          value={conceptId || ""}
          onChange={(e) => purgeAndRestart(e.target.value)}
          className="border border-[var(--iddl-rule)] bg-white px-3 py-2 text-sm rounded-none min-w-[380px]"
          data-testid="engine-preview-concept-select"
        >
          {catalogue.length === 0 && (
            <option value="">(no concepts activated — flip use_new_engine in Assessment Contracts)</option>
          )}
          {catalogue.map((c) => {
            const richLabel = c.environments.some((e) => e.rich) ? "" : " · GENERIC PREVIEW";
            return (
              <option key={c.id} value={c.id}>
                {c.id} · {c.name}{richLabel}
              </option>
            );
          })}
        </select>
        <span className="label-mono text-xs text-[var(--iddl-muted)]"
              data-testid="engine-preview-concept-count">
          {catalogue.length} concepts
        </span>

        <label className="label-mono" htmlFor="engine-preview-env">Environment:</label>
        <select
          id="engine-preview-env"
          value={environment || ""}
          onChange={(e) => purgeAndRestart(conceptId, e.target.value || null)}
          className="border border-[var(--iddl-rule)] bg-white px-3 py-2 text-sm rounded-none min-w-[160px]"
          data-testid="engine-preview-env-select"
          title="Assessment environment (only environments that have a contract for this concept are enabled)"
        >
          {(() => {
            const entry = catalogue.find((c) => c.id === conceptId);
            const envs = entry?.environments || [];
            // Show only environments the concept actually has.
            if (envs.length === 0) {
              return <option value="">(pick a concept first)</option>;
            }
            return envs.map((e) => {
              const label =
                e.env === "agentic_ai" ? "Agentic AI"
                : e.env === "ai" ? "AI"
                : "(no environment)";
              return (
                <option key={e.env || "null"} value={e.env || ""}>
                  {label} · {e.sim}
                </option>
              );
            });
          })()}
        </select>
        {/* Phase 10 · Assessment Variant selector. Default = candidate-facing. */}
        <label className="label-mono ms-4" htmlFor="engine-preview-variant">Variant:</label>
        <select
          id="engine-preview-variant"
          value={variantId || ""}
          onChange={(e) => {
            const vid = e.target.value || null;
            setVariantId(vid);
            if (!vid) {
              setVariantOverride(null);
              // Phase 11 · Default = canonical candidate-facing payload.
              purgeAndRestart(conceptId, environment, undefined, undefined, null);
              return;
            }
            const v = variantList.find((x) => x.variant_id === vid);
            if (!v) { setVariantOverride(null); return; }
            setVariantOverride(v);
            // Phase 11 · Mount the complete variant payload against a
            // fresh preview attempt. Scoring still uses the canonical
            // capability contract — see EnginePreview.start().
            purgeAndRestart(conceptId, environment, undefined, undefined, v);
          }}
          className="border border-[var(--iddl-rule)] bg-white px-3 py-2 text-sm rounded-none min-w-[220px]"
          data-testid="engine-preview-variant-select"
          title="Phase 10 · pick an Assessment Variant to preview. Default = candidate-facing. Draft and Approved variants can be previewed without affecting live candidates."
        >
          <option value="">● Default · active (candidate-facing)</option>
          {variantList.filter(v => !v.is_default).map(v => (
            <option key={v.variant_id} value={v.variant_id}>
              {v.variant_name} — {v.status.toUpperCase()}
            </option>
          ))}
        </select>
        {variantOverride && (
          <span className="label-mono text-[11px] px-2 py-1 bg-amber-100 text-amber-900" data-testid="variant-preview-banner">
            PREVIEW ONLY · NOT CANDIDATE-FACING
          </span>
        )}
        <label className="label-mono ms-4" htmlFor="engine-preview-country">Country:</label>
        <select
          id="engine-preview-country"
          value={previewCountry}
          onChange={(e) => {
            setPreviewCountry(e.target.value);
            purgeAndRestart(conceptId, undefined, e.target.value);
          }}
          className="border border-[var(--iddl-rule)] bg-white px-3 py-2 text-sm rounded-none"
          data-testid="engine-preview-country-select"
          title="Rehearse the candidate experience for this country (DS-IN-01 scenario re-localizes)"
        >
          <option value="United Kingdom">United Kingdom</option>
          <option value="Jordan">Jordan</option>
          <option value="New Zealand">New Zealand</option>
          <option value="United Arab Emirates">United Arab Emirates</option>
        </select>

        <label className="label-mono ms-4" htmlFor="engine-preview-lang">Language:</label>
        <select
          id="engine-preview-lang"
          value={previewLang}
          onChange={(e) => {
            setPreviewLang(e.target.value);
            purgeAndRestart(conceptId, undefined, undefined, e.target.value);
          }}
          className="border border-[var(--iddl-rule)] bg-white px-3 py-2 text-sm rounded-none"
          data-testid="engine-preview-lang-select"
          title="Rehearse the candidate experience in this language (en / ar / mi). Assessment state is preserved across language switches."
        >
          <option value="en">English (en)</option>
          <option value="ar">العربية (ar)</option>
          <option value="mi">te reo Māori (mi)</option>
        </select>
      </div>

      {err && (
        <div className="mb-4 text-sm text-rose-700 border border-rose-300 bg-rose-50 p-2" data-testid="engine-preview-error">
          {err}
        </div>
      )}

      {loading && (
        <div className="text-sm text-[var(--iddl-muted)]">{t("common.loading")}</div>
      )}

      {config && attempt && (
        <>
          <div className="flex items-center flex-wrap gap-2 mb-3" data-testid="engine-preview-metadata">
            <div className="label-mono">
              <strong>{conceptId}</strong> · env=
              <code data-testid="engine-preview-meta-env">{config.environment || "null"}</code> · primitive=
              <code data-testid="engine-preview-meta-primitive">{config.simulation_type}</code> · canonical v
              <span data-testid="engine-preview-meta-version">{canonMeta?.config_version ?? config.config_version}</span> (
              <code data-testid="engine-preview-meta-hash">{canonMeta?.config_hash ?? config.config_hash}</code>) · attempt <code>{attempt.id}</code>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-none ms-auto"
              onClick={computeScore}
              data-testid="btn-engine-score"
            >
              <Calculator size={14} className="mr-2" /> Compute score
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-none border-rose-600 text-rose-700"
              onClick={() => purgeAndRestart(conceptId, environment, undefined, undefined, variantOverride || null)}
              data-testid="btn-engine-restart"
            >
              <Trash2 size={14} className="mr-2" /> Purge + restart
            </Button>
          </div>

          {/* Phase 11 · variant diagnostic panel. Always visible so the
              admin can confirm exactly which Default/variant is mounted
              and that the canonical capability contract is unchanged. */}
          <div className="border border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3 mb-3 text-xs grid md:grid-cols-3 gap-x-6 gap-y-1" data-testid="variant-diagnostic-panel">
            <div><span className="label-mono text-[var(--iddl-muted)]">variant_id · </span><code data-testid="diag-variant-id">{config._variant?.variant_id || `VAR-${conceptId}-${config.environment || "null"}-default`}</code></div>
            <div><span className="label-mono text-[var(--iddl-muted)]">variant_name · </span><span data-testid="diag-variant-name">{config._variant?.variant_name || `${conceptId} ${config.concept_name || ""} — Default`}</span></div>
            <div><span className="label-mono text-[var(--iddl-muted)]">country · </span><span data-testid="diag-country">{config._variant?.country || config.candidate_country || "—"}</span></div>
            <div><span className="label-mono text-[var(--iddl-muted)]">status · </span><code data-testid="diag-status">{(config._variant?.status || "default · active").toUpperCase()}</code></div>
            <div><span className="label-mono text-[var(--iddl-muted)]">concept_code · </span><code data-testid="diag-concept-code">{conceptId}</code></div>
            <div><span className="label-mono text-[var(--iddl-muted)]">environment · </span><code data-testid="diag-environment">{config.environment || "null"}</code></div>
            <div><span className="label-mono text-[var(--iddl-muted)]">primitive_id · </span><code data-testid="diag-primitive-id">{config.simulation_type}</code></div>
            <div><span className="label-mono text-[var(--iddl-muted)]">canonical contract version · </span><code data-testid="diag-canonical-version">{canonMeta?.config_version ?? "—"}</code></div>
            <div className="md:col-span-3"><span className="label-mono text-[var(--iddl-muted)]">canonical contract hash · </span><code className="break-all" data-testid="diag-canonical-hash">{canonMeta?.config_hash ?? "—"}</code></div>
          </div>

          {/* Phase 11.2 · Knowledge panel for non-default variants.
              Default variants fetch Knowledge via effective_questions
              on the candidate side; Engine Preview intentionally does
              not render a Default Knowledge control so the preview
              surface for Default remains byte-identical to its
              pre-11.2 behaviour. */}
          {variantOverride && !variantOverride.is_default && variantOverride.knowledge && (variantOverride.knowledge.options || []).length > 0 && (
            <div className="border border-[var(--iddl-rule)] bg-white p-3 mb-3" data-testid="variant-knowledge-panel">
              <div className="label-mono text-[var(--iddl-muted)] mb-1">Knowledge · variant</div>
              <div className="text-sm font-medium mb-2" data-testid="variant-knowledge-question">{variantOverride.knowledge.question_text}</div>
              <div className="space-y-1">
                {(variantOverride.knowledge.options || []).map((opt, i) => (
                  <label key={i} className="flex items-start gap-2 text-sm" data-testid={`variant-knowledge-option-${i}`}>
                    <input
                      type="radio"
                      name="variant-knowledge"
                      checked={previewKnowledgeIndex === i}
                      onChange={() => setPreviewKnowledgeIndex(i)}
                      data-testid={`variant-knowledge-radio-${i}`}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {(config.candidate_goal || config.candidate_task) && (
            <div className="border-l-4 border-[var(--iddl-ink)] bg-[var(--iddl-paper)] px-3 py-2 mb-3 text-sm" data-testid="engine-candidate-goal">
              {config.candidate_task && (
                <div className="mb-2" data-testid="engine-candidate-task">
                  <span className="label-mono text-[var(--iddl-muted)] mr-2">Task ·</span>
                  <span>{config.candidate_task}</span>
                </div>
              )}
              {config.candidate_goal && (
                <div>
                  <span className="label-mono text-[var(--iddl-muted)] mr-2">Goal ·</span>
                  <span>{config.candidate_goal}</span>
                </div>
              )}
            </div>
          )}

          <div className="grid lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8">
              {config.simulation_type === "iddl_inbox" && (
                <IDDLInbox
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_browser" && (
                <IDDLBrowser
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_ai_chat" && (
                <IDDLAIChat
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_ai_evaluate" && (
                <IDDLAIEvaluate
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_ai_decision" && (
                <IDDLAIDecision
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_ai_chat_agent" && (
                <IDDLAIChatAgent
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  environment={config.environment}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_ai_evaluate_agent" && (
                <IDDLAIEvaluateAgent
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  environment={config.environment}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_ai_decision_agent" && (
                <IDDLAIDecisionAgent
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  environment={config.environment}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_file_manager" && (
                <IDDLFileManager
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_settings" && (
                <IDDLSettings
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_workflow" && (
                <IDDLWorkflow
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {config.simulation_type === "iddl_editor" && (
                <IDDLEditor
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  onEventLogged={handleEvent}
                />
              )}
              {!SHIPPED_PRIMITIVES.has(config.simulation_type) && (
                <IDDLGenericPrimitive
                  attemptId={attempt.id}
                  conceptId={conceptId}
                  scenarioText={config.scenario_text}
                  runtimeConfig={config}
                  onEventLogged={handleEvent}
                />
              )}
            </div>

            <aside className="lg:col-span-4 border border-[var(--iddl-rule)]" data-testid="engine-event-log">
              <div className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
                <span className="font-display font-semibold">Behaviour log</span>
                <span className="label-mono ms-auto">{events.length} events</span>
              </div>
              <div className="px-3 py-2 border-b border-[var(--iddl-rule)] text-xs grid grid-cols-3 gap-2 bg-[var(--iddl-paper)]">
                <div>
                  <div className="label-mono">total dwell</div>
                  <div className="font-mono">{(totalDwellMs / 1000).toFixed(1)}s</div>
                </div>
                <div>
                  <div className="label-mono">reversals</div>
                  <div className="font-mono">{reversals}</div>
                </div>
                <div>
                  <div className="label-mono">auto-fail</div>
                  <div className={`font-mono ${autoFails ? "text-rose-700 font-bold" : ""}`}>
                    {autoFails}
                  </div>
                </div>
              </div>
              <ul className="max-h-[480px] overflow-y-auto divide-y divide-[var(--iddl-rule)]" data-testid="engine-event-list">
                {events.map((e) => (
                  <li
                    key={e.id}
                    className="px-3 py-2 text-xs"
                    data-testid={`engine-event-${e.event_order}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="label-mono">#{e.event_order}</span>
                      <code className="font-mono font-semibold">{e.event_name}</code>
                      {e.is_required_event && (
                        <span className="label-mono px-1 border border-emerald-300 text-emerald-700">REQ</span>
                      )}
                      {e.is_critical_event && (
                        <span className="label-mono px-1 border border-amber-300 text-amber-700">CRIT</span>
                      )}
                      {e.is_auto_fail_event && (
                        <span className="label-mono px-1 border border-rose-400 text-rose-700">AUTO_FAIL</span>
                      )}
                      {e.is_reversal && (
                        <span className="label-mono px-1 border border-violet-300 text-violet-700">REVERSAL</span>
                      )}
                      <span className="label-mono ms-auto">
                        {e.dwell_ms != null ? `Δ${e.dwell_ms}ms` : ""}
                      </span>
                    </div>
                    {e.hesitation_ms != null && (
                      <div className="label-mono mt-1 text-rose-700">
                        hesitation: {e.hesitation_ms}ms
                      </div>
                    )}
                    {e.interaction_count > 0 && (
                      <div className="label-mono mt-1">
                        repeated inspection: #{e.interaction_count + 1}
                      </div>
                    )}
                    {e.event_value && (
                      <pre className="mt-1 whitespace-pre-wrap text-[10px] text-[var(--iddl-muted)]">
                        {JSON.stringify(e.event_value).slice(0, 200)}
                      </pre>
                    )}
                  </li>
                ))}
                {events.length === 0 && (
                  <li className="px-3 py-3 text-xs text-[var(--iddl-muted)]">
                    No events yet — interact with the primitive to start.
                  </li>
                )}
              </ul>
            </aside>
          </div>

          {/* Phase 11.2 · Reflection panel for non-default variants. */}
          {variantOverride && !variantOverride.is_default && variantOverride.reflection?.prompt && (
            <div className="border border-[var(--iddl-rule)] bg-white p-3 mt-3" data-testid="variant-reflection-panel">
              <div className="label-mono text-[var(--iddl-muted)] mb-1">Reflection · variant</div>
              <div className="text-sm mb-2" data-testid="variant-reflection-prompt">{variantOverride.reflection.prompt}</div>
              <textarea
                className="w-full border border-[var(--iddl-rule)] p-2 text-sm font-mono"
                rows={4}
                placeholder="Write the candidate's reflection response here…"
                value={previewReflectionText}
                onChange={(e) => setPreviewReflectionText(e.target.value)}
                data-testid="variant-reflection-textarea"
              />
            </div>
          )}

          {scoreResult && (
            <>
              <div className="mt-4 border border-[var(--iddl-rule)] p-4 bg-[var(--iddl-paper)]" data-testid="engine-score-result">
                <div className="flex items-center gap-3">
                  <Calculator size={14} />
                  <span className="font-display font-semibold">Deterministic score</span>
                  <span
                    className={`label-mono px-2 py-1 ms-auto ${
                      scoreResult.score.outcome === "Merit" ? "border border-emerald-400 text-emerald-800 bg-emerald-50"
                        : scoreResult.score.outcome === "Pass" ? "border border-sky-400 text-sky-800 bg-sky-50"
                        : "border border-rose-400 text-rose-800 bg-rose-50"
                    }`}
                    data-testid="engine-score-outcome"
                  >
                    {scoreResult.score.outcome.toUpperCase()}
                  </span>
                </div>
                {/* Phase 11.2 · when the variant preview_attempt
                    endpoint ran, surface the per-layer K / C / R
                    outcomes + aggregate so admins can audit the
                    complete deterministic result in one place. */}
                {scoreResult.layers && (
                  <div className="mt-2 text-xs flex items-center gap-3 flex-wrap" data-testid="engine-layer-outcomes">
                    <span className="label-mono">K · <code data-testid="engine-layer-k">{scoreResult.layers.knowledge?.outcome}</code> ({scoreResult.layers.knowledge?.score}/{scoreResult.layers.knowledge?.max_score})</span>
                    <span className="label-mono">C · <code data-testid="engine-layer-c">{scoreResult.layers.capability?.outcome}</code> ({scoreResult.layers.capability?.score}/{scoreResult.layers.capability?.max_score})</span>
                    <span className="label-mono">R · <code data-testid="engine-layer-r">{scoreResult.layers.reflection?.outcome}</code> ({scoreResult.layers.reflection?.score}/{scoreResult.layers.reflection?.max_score})</span>
                    {scoreResult.variant_outcome && (
                      <span className="label-mono">aggregate · <code data-testid="engine-layer-aggregate">{scoreResult.variant_outcome}</code></span>
                    )}
                  </div>
                )}
                <div className="mt-2 text-xs grid lg:grid-cols-2 gap-2">
                  <div>
                    <div className="label-mono">reasons</div>
                    <ul className="list-disc pl-5">
                      {scoreResult.score.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="label-mono">final_decision</div>
                    <code>{scoreResult.score.final_decision || "—"}</code>
                    <div className="label-mono mt-2">required satisfied</div>
                    <div>{scoreResult.score.required_satisfied.join(", ") || "—"}</div>
                    <div className="label-mono mt-2">critical satisfied</div>
                    <div>{scoreResult.score.critical_satisfied.join(", ") || "—"}</div>
                    {scoreResult.score.auto_fail_fired.length > 0 && (
                      <>
                        <div className="label-mono mt-2 text-rose-700">auto_fail fired</div>
                        <div className="text-rose-700">{scoreResult.score.auto_fail_fired.join(", ")}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* ── REMEDIATION REVIEW PANEL ─────────────────────────────
                  Surfaces (a) exactly why the candidate would fail or
                  miss merit, (b) which required/critical/auto_fail
                  events drove that, and (c) the literal feedback string
                  the candidate sees on their result screen — letting an
                  admin verify fairness, scoring transparency, and
                  remediation alignment in one place. */}
              <RemediationReview
                score={scoreResult.score}
                config={config}
                events={events}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}


function RemediationReview({ score, config, events }) {
  const admin = (config && config._admin_full) || {};
  const required = admin.required_events || [];
  const critical = admin.critical_merit_events || [];
  const autoFail = admin.auto_fail_events || [];
  const fired = new Set(events.map((e) => e.event_name));
  const passed = score.outcome === "Pass" || score.outcome === "Merit";

  // Phase 11 · the authored remediation message comes from the variant
  // if one is mounted; otherwise from the canonical concept. The
  // learning-module prefix is composed at THIS presentation layer so
  // the stored message never contains the prefix.
  const variantRem = config?._variant?.remediation_message;
  const canonicalRem = admin.remediation_message || "";
  const authoredRem = (variantRem && variantRem.trim()) || canonicalRem;
  const prefixedRemediation = applyLearningModulePrefix(
    config?.concept_id, config?.concept_name, authoredRem,
  );

  // Compose missing/triggered lists.
  const requiredFired = required.filter((e) => fired.has(e));
  const requiredMissing = required.filter((e) => !fired.has(e));
  const criticalFired = critical.filter((e) => fired.has(e));
  const criticalMissing = critical.filter((e) => !fired.has(e));
  const autoFailFired = autoFail.filter((e) => fired.has(e));

  // Sequence-rule violations: any `before` event that fired after its
  // `after` partner (or fired without the after partner at all).
  const sequenceRules = admin.sequence_rules || [];
  const seqViolations = [];
  if (sequenceRules.length > 0 && events.length > 0) {
    const orderByEvent = {};
    events.forEach((e) => {
      if (orderByEvent[e.event_name] == null) {
        orderByEvent[e.event_name] = e.event_order;
      }
    });
    for (const rule of sequenceRules) {
      const b = orderByEvent[rule.before];
      const a = orderByEvent[rule.after];
      if (b != null && a != null && b > a) {
        seqViolations.push(`${rule.before} fired AFTER ${rule.after}`);
      }
    }
  }

  return (
    <div
      className={`mt-4 border p-4 ${passed ? "border-emerald-300 bg-emerald-50/30" : "border-rose-300 bg-rose-50/40"}`}
      data-testid="remediation-review-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <Wrench size={14} className={passed ? "text-emerald-700" : "text-rose-700"} />
        <span className="font-display font-semibold">Remediation review</span>
        <span className="label-mono text-[var(--iddl-muted)] ms-auto">
          {passed ? "Candidate would pass" : "Candidate would fail / not meet merit"}
        </span>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 text-xs">
        <RemediationBlock
          title="Required events"
          satisfied={requiredFired}
          missing={requiredMissing}
          testidPrefix="rem-required"
          missingLabel="not fired (drives FAIL by omission)"
        />
        <RemediationBlock
          title="Critical (merit) events"
          satisfied={criticalFired}
          missing={criticalMissing}
          testidPrefix="rem-critical"
          missingLabel="not fired (drives Pass instead of Merit)"
        />
        <RemediationBlock
          title="Auto-fail events"
          satisfied={[]}
          missing={autoFail}
          testidPrefix="rem-autofail"
          missingLabel="not triggered (good)"
          triggered={autoFailFired}
          triggeredLabel="TRIGGERED (instant FAIL)"
        />
        <div data-testid="rem-sequence">
          <div className="label-mono mb-1">Sequence-rule violations</div>
          {seqViolations.length === 0 ? (
            <div className="text-emerald-700">None — event order respects every rule.</div>
          ) : (
            <ul className="list-disc pl-5 text-rose-700">
              {seqViolations.map((v, i) => <li key={i}>{v}</li>)}
            </ul>
          )}
          {sequenceRules.length === 0 && (
            <div className="text-[var(--iddl-muted)] italic">No sequence rules authored.</div>
          )}
        </div>
      </div>

      {authoredRem ? (
        <div className="mt-4 pt-3 border-t border-[var(--iddl-rule)]" data-testid="rem-candidate-feedback">
          <div className="label-mono mb-1 flex items-center gap-2">
            <AlertTriangle size={12} />
            Candidate-facing remediation message
          </div>
          <p className="text-sm leading-relaxed italic bg-white border border-[var(--iddl-rule)] p-3" data-testid="rem-candidate-feedback-text">
            {prefixedRemediation}
          </p>
        </div>
      ) : (
        <div className="mt-4 pt-3 border-t border-[var(--iddl-rule)] text-xs text-amber-700" data-testid="rem-no-message">
          <AlertTriangle size={12} className="inline mr-2" />
          No `remediation_message` authored for this contract — candidates
          who fail will see only the generic placeholder. Author it in
          Assessment Contracts → Edit contract.
        </div>
      )}

      {score.multi_object?.enabled && (
        <div className="mt-4 pt-3 border-t border-[var(--iddl-rule)]" data-testid="rem-multi-object">
          <div className="label-mono mb-2">
            Multi-object verdict (per message)
            <span className="text-[var(--iddl-muted)] font-normal ml-2">
              · {score.multi_object.total_threats} threat
              {score.multi_object.total_threats === 1 ? "" : "s"} of{" "}
              {score.multi_object.total_objects} total
            </span>
          </div>
          <table className="text-xs w-full">
            <thead>
              <tr className="text-left text-[var(--iddl-muted)]">
                <th className="label-mono pr-3">id</th>
                <th className="label-mono pr-3">truth</th>
                <th className="label-mono pr-3">opened</th>
                <th className="label-mono pr-3">candidate action</th>
                <th className="label-mono">verdict</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(score.multi_object.per_object || {}).map((obj) => {
                // Iter-25.2 — verdict semantics. Six outcomes cover
                // every (truth × toolbar-action) pair so the auditor
                // can see at a glance whether the candidate's per-email
                // choice was correct / a false positive / a missed
                // threat / an unsafe interaction / engaged correctly /
                // not triaged at all.
                const VERDICT_LABEL = {
                  correct_phishing_neutralised: { label: "CORRECT (phishing handled)", cls: "text-emerald-700" },
                  correct_legit_kept:           { label: "CORRECT (legit kept)",        cls: "text-emerald-700" },
                  correct_legit_engaged:        { label: "CORRECT (legit engaged)",     cls: "text-emerald-700" },
                  missed_phishing:              { label: "MISSED THREAT",               cls: "text-rose-700 font-semibold" },
                  missed_phishing_unsafe_action:{ label: "MISSED + UNSAFE (replied/forwarded a threat)", cls: "text-rose-700 font-semibold" },
                  false_positive_on_legit:      { label: "FALSE POSITIVE",              cls: "text-rose-700 font-semibold" },
                  not_triaged:                  { label: "NOT TRIAGED",                 cls: "text-amber-700 font-semibold" },
                };
                const v = obj.verdict || "not_triaged";
                const meta = VERDICT_LABEL[v] || { label: v, cls: "text-zinc-700" };
                const ACTION_PRETTY = {
                  message_reported: "Reported",
                  message_archived: "Archived",
                  message_deleted:  "Deleted",
                  reply_drafted:    "Reply (drafted)",
                  reply_sent:       "Reply (sent)",
                  forward_drafted:  "Forward (drafted)",
                  forward_sent:     "Forward (sent)",
                };
                const decisionPretty = obj.last_toolbar_action
                  ? (ACTION_PRETTY[obj.last_toolbar_action] || obj.last_toolbar_action)
                  : "—";
                return (
                  <tr key={obj.id} data-testid={`rem-mo-${obj.id}`}>
                    <td className="pr-3 font-mono">{obj.id}</td>
                    <td className="pr-3">{obj.is_threat ? "phishing" : "legitimate"}</td>
                    <td className="pr-3">{obj.inspected ? "✔" : "✖"}</td>
                    <td className="pr-3 italic">{decisionPretty}</td>
                    <td className={meta.cls}>{meta.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RemediationBlock({ title, satisfied, missing, missingLabel, testidPrefix, triggered, triggeredLabel }) {
  return (
    <div data-testid={testidPrefix}>
      <div className="label-mono mb-1">{title}</div>
      {satisfied.length > 0 && (
        <div className="mb-1">
          <span className="label-mono text-emerald-700 mr-2">✔ FIRED:</span>
          <span>{satisfied.join(", ")}</span>
        </div>
      )}
      {triggered && triggered.length > 0 && (
        <div className="mb-1">
          <span className="label-mono text-rose-700 mr-2">⛔ {triggeredLabel}:</span>
          <span className="text-rose-700 font-semibold">{triggered.join(", ")}</span>
        </div>
      )}
      {missing.length > 0 && (
        <div>
          <span className="label-mono text-[var(--iddl-muted)] mr-2">✖ {missingLabel}:</span>
          <span>{missing.join(", ")}</span>
        </div>
      )}
      {satisfied.length === 0 && missing.length === 0 && !(triggered && triggered.length > 0) && (
        <div className="text-[var(--iddl-muted)] italic">None authored.</div>
      )}
    </div>
  );
}
